import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Bell, Loader2, LogOut, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth } from "./AuthContext";
import { api } from "../lib/api";
import { Logo } from "../components/Brand";
import LeadsTab from "./LeadsTab";
import AnalyticsTab from "./AnalyticsTab";
import ProductsTab from "./ProductsTab";
import SettingsTab from "./SettingsTab";
import OrdersTab from "./OrdersTab";
import ShopsTab from "./ShopsTab";

const STAT_LABELS = [
  ["visitors", "Website visitors"],
  ["total", "Total enquiries"],
  ["today", "New leads today"],
  ["month", "Leads this month"],
  ["hot", "Hot leads"],
  ["follow_up", "Follow-up pending"],
  ["converted", "Converted"],
  ["lost", "Lost leads"],
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);
  const latest = useRef(null);

  const loadStats = useCallback(() => {
    api.get("/crm/stats").then((r) => {
      const d = r.data ?? {};
      if (latest.current && d.latest && d.latest > latest.current) {
        toast.success("New enquiry received!", { icon: <Bell className="h-4 w-4" /> });
        setRefreshKey((k) => k + 1);
      }
      latest.current = d.latest || latest.current || "0";
      setStats(d);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    loadStats();
    const t = setInterval(loadStats, 30000);
    return () => clearInterval(t);
  }, [user, loadStats]);

  if (user === null) return <div className="grid min-h-screen place-items-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;
  if (user === false) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[var(--paper)]">
      <header className="bg-navy">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 lg:px-8">
          <Logo />
          <div className="flex items-center gap-2">
            <Link to="/" className="hidden sm:inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm text-slate-300 hover:text-white"><ExternalLink className="h-4 w-4" /> View site</Link>
            <button data-testid="admin-logout-button" onClick={logout} className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-4 py-2 text-sm font-semibold text-white hover:border-brand"><LogOut className="h-4 w-4" /> Logout</button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-5 py-10 lg:px-8">
        <h1 className="font-display text-4xl font-extrabold uppercase text-navy">Dashboard</h1>
        <p className="text-sm text-slate-600">Signed in as {user.email}</p>
        <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-4">
          {STAT_LABELS.map(([k, l]) => (
            <div key={k} data-testid={`admin-stat-${k}`} className="rounded-2xl border border-navy/10 bg-white p-5">
              <p className="font-display text-4xl font-extrabold text-navy">{stats?.[k] ?? 0}</p>
              <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-slate-500">{l}</p>
            </div>
          ))}
        </div>
        <Tabs defaultValue="enquiries" className="mt-10">
          <TabsList className="h-auto flex-wrap bg-white border border-navy/10">
            <TabsTrigger value="enquiries" data-testid="admin-tab-enquiries">Leads{stats?.new ? ` (${stats.new} new)` : ""}</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="admin-tab-analytics">Visitor analytics</TabsTrigger>
            <TabsTrigger value="orders" data-testid="admin-tab-orders">Orders</TabsTrigger>
            <TabsTrigger value="products" data-testid="admin-tab-products">Products</TabsTrigger>
            <TabsTrigger value="shops" data-testid="admin-tab-shops">Shops</TabsTrigger>
            <TabsTrigger value="settings" data-testid="admin-tab-settings">Settings</TabsTrigger>
          </TabsList>
          <TabsContent value="enquiries"><LeadsTab onChange={loadStats} refreshKey={refreshKey} /></TabsContent>
          <TabsContent value="analytics"><AnalyticsTab /></TabsContent>
          <TabsContent value="orders"><OrdersTab onChange={loadStats} /></TabsContent>
          <TabsContent value="products"><ProductsTab onChange={loadStats} /></TabsContent>
          <TabsContent value="shops"><ShopsTab onChange={loadStats} /></TabsContent>
          <TabsContent value="settings"><SettingsTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
